import { Directive, ElementRef, forwardRef, HostListener, inject, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const fracty = require('fracty');

@Directive({
  selector: '[decimalToFraction]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalToFractionDirective),
      multi: true,
    },
  ],
})
export class DecimalToFractionDirective implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLInputElement>);
  private readonly renderer = inject(Renderer2);

  private isFocused = false;
  // onChange emits the raw decimal string (e.g. "15.5") or '' for empty,
  // matching the string-backed form controls used across the app.
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @HostListener('focus')
  onFocus(): void {
    this.isFocused = true;
    const current = this.el.nativeElement.value;
    const numeric = this.parseFractionToDecimal(current);
    const display = numeric !== null ? this.toDecimalString(numeric) : '';
    this.setInputValue(display);
  }

  @HostListener('input', ['$event.target.value'])
  onInput(raw: string): void {
    // Skip form updates for in-progress inputs: trailing dot, space (starting
    // a mixed fraction), slash, or "whole numerator" with no slash yet.
    if (this.isInProgressInput(raw)) {
      return;
    }
    // parseFractionToDecimal handles both "15.5" and "1 1/2".
    const numeric = this.parseFractionToDecimal(raw);
    this.onChange(numeric !== null ? this.toDecimalString(numeric) : '');
  }

  @HostListener('blur', ['$event.target.value'])
  onBlur(raw: string): void {
    this.isFocused = false;
    this.onTouched();

    const numeric = this.parseFractionToDecimal(raw);

    if (numeric === null) {
      this.setInputValue('');
      this.onChange('');
      return;
    }

    this.setInputValue(fracty(numeric));
    // Emit the normalized decimal string, not a number.
    this.onChange(this.toDecimalString(numeric));
  }

  writeValue(value: string | number | null): void {
    if (value === null || value === undefined) {
      this.setInputValue('');
      return;
    }

    const str = String(value);

    if (!str.trim()) {
      this.setInputValue('');
      return;
    }

    // While the user is actively editing, show the raw decimal so they can
    // see what they're typing. Fraction conversion happens on blur.
    if (this.isFocused) {
      this.setInputValue(str);
      return;
    }

    // Guard against a trailing '.' (e.g. "15." from a virtual keyboard).
    if (str.endsWith('.')) {
      this.setInputValue(str);
      return;
    }

    const numeric = Number(value);
    this.setInputValue(isNaN(numeric) ? '' : fracty(numeric));
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  private isInProgressInput(raw: string): boolean {
    if (!raw) {
      return false;
    }
    // Trailing dot  → "15."   (decimal in progress)
    // Trailing space → "1 "   (about to type mixed-fraction numerator)
    // Trailing slash → "1 1/" (denominator not yet typed)
    // "N M" pattern  → "1 1"  (numerator typed, slash not yet typed)
    return raw.endsWith('.') || raw.endsWith(' ') || raw.endsWith('/') || /^\d+\s+\d+$/.test(raw);
  }

  private setInputValue(value: string): void {
    this.renderer.setProperty(this.el.nativeElement, 'value', value);
  }

  private toDecimalString(value: number): string {
    return String(parseFloat(value.toFixed(3)));
  }

  private parseFractionToDecimal(display: string): number | null {
    const trimmed = display?.trim();
    if (!trimmed) {
      return null;
    }

    if (!trimmed.includes('/')) {
      const asFloat = parseFloat(trimmed);
      return isNaN(asFloat) ? null : asFloat;
    }

    return this.evaluateFractionString(trimmed);
  }

  private evaluateFractionString(text: string): number | null {
    const mixedMatch = /^(\d+)\s+(\d+)\/(\d+)$/.exec(text);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1], 10);
      const numerator = parseInt(mixedMatch[2], 10);
      const denominator = parseInt(mixedMatch[3], 10);
      return denominator !== 0 ? whole + numerator / denominator : null;
    }

    const fractionMatch = /^(\d+)\/(\d+)$/.exec(text);
    if (fractionMatch) {
      const numerator = parseInt(fractionMatch[1], 10);
      const denominator = parseInt(fractionMatch[2], 10);
      return denominator !== 0 ? numerator / denominator : null;
    }

    return null;
  }
}
